import React from 'react';
import Header from '@/components/header/Header';
import Footer from '@/components/footer/Footer';
import WithStandardLayout from '@/layouts/withStandard.layout';
import WithReferralLayout from '@/layouts/withReferral.layout';
import useAccount from '@/hooks/useAccount';
import initializeIconLibrary from '@/utils/fontAwesomeLibrary';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-loading-skeleton/dist/skeleton.css';
import OpportunityPage from '@/components/OpportunityPage/OpportunityPage';
import SeoMetadata from '@/components/SeoMetadata/SeoMetadata';
import { buildServerSideProps } from '@/utils/server';
import ServerODRS from '@/utils/api/odrs/server';

initializeIconLibrary();
const Opportunities = ({ resource: initialResource }) => {
  const { account } = useAccount();
  const resource = initialResource || {};
  const isPro = !!account?.data?.is_professional;

  return (
    <>
      <SeoMetadata resource={resource} />
      <Header />
      {/* Keyed so navigating between two /opp/ pages remounts the tree and re-renders all sections with the new opp data */}
      <OpportunityPage key={resource.id} account={account} resource={resource} isPro={isPro} />
      <Footer />
    </>
  );
};

export const getServerSideProps = buildServerSideProps(async (context, args) => {
  const { opportunityId } = context.params;
  try {
    const { data } = await ServerODRS.getResourceById('opportunities', opportunityId, { with_property_labels: true });
    if (!data?.id) return { notFound: true };
    // Redirect to the canonical slug for SEO if routing an ID or a stale slug (ODRS resolves slug history via friendly_id)
    // Preserve the query string (e.g. ?info=correct&source=pro_email) so params survive the hop.
    if (data.slug && opportunityId !== data.slug) {
      const { opportunityId: _opportunityId, ...rest } = context.query;
      const qs = new URLSearchParams();
      Object.entries(rest).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => qs.append(key, v));
        } else if (value !== undefined) {
          qs.append(key, value);
        }
      });
      const queryString = qs.toString();
      return {
        redirect: {
          destination: `/opp/${data.slug}${queryString ? `?${queryString}` : ''}`,
          // `permanent` controls the HTTP status code of the redirect
          // with true resolving as a 308 Permanent Redirect and false as a 307 Temporary Redirect
          // Report the redirect as permanent if it is a redirect from Opp ID --> Opp Slug because IDs never change
          permanent: /^\d+$/.test(opportunityId)
        }
      };
    }
    args.props.resource = data;
    // Match the Angular resource page title: "<opp title> - <org name>".
    args.props.pageTitle = data.organization?.name ? `${data.title} - ${data.organization.name}` : data.title;
  } catch (error) {
    return { notFound: true };
  }
  return args;
});

Opportunities.getLayout = function getLayout(page) {
  return (
    <WithStandardLayout host={page.props.host}>
      <WithReferralLayout>{page}</WithReferralLayout>
    </WithStandardLayout>
  );
};

export default Opportunities;
